import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ShoppingBag, Sparkles, Moon, Leaf, Droplets, Bed, ChevronDown, Star, StarHalf, ShoppingCart, Wind } from 'lucide-react';
import { Product } from './Boutique';
import { useClickSound } from '../hooks/useClickSound';
interface ProductDetailProps {
  product: Product;
  onClose: () => void;
}
// Helper to map string icon names to Lucide components
const getFeatureIcon = (iconName: string) => {
  switch (iconName) {
    case 'Leaf':
      return <Leaf className="w-5 h-5" data-id="element-854" />;
    case 'Droplets':
      return <Droplets className="w-5 h-5" data-id="element-855" />;
    case 'Moon':
      return <Moon className="w-5 h-5" data-id="element-856" />;
    case 'Bed':
      return <Bed className="w-5 h-5" data-id="element-857" />;
    case 'Wind':
      return <Wind className="w-5 h-5" data-id="element-858" />;
    default:
      return <Sparkles className="w-5 h-5" data-id="element-859" />;
  }
};
// Accordion Component
const Accordion = ({
  title,
  children,
  defaultOpen = false
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const {
    playClick
  } = useClickSound();
  return <div className="border-t border-slate-100 last:border-b" data-id="element-860">
      <button onClick={() => {
      playClick();
      setIsOpen(!isOpen);
    }} className="w-full py-4 flex justify-between items-center hover:bg-slate-50/50 transition-colors" data-id="element-861">
        <span className="font-bold text-sm text-zen-text" data-id="element-862">{title}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-863">
          <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-864" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-865">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-866">
            <div className="pb-4 text-xs text-slate-500 leading-relaxed" data-id="element-867">
              {children}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function ProductDetail({
  product,
  onClose
}: ProductDetailProps) {
  const {
    playClick
  } = useClickSound();
  // Extract numeric energy value
  const energyValue = product.energy.replace('+', '');
  const handleAddToCart = () => {
    playClick();
    alert(`Produkt dodany do koszyka! Otrzymasz +${energyValue} Punktów Energii.`);
  };
  return <motion.div className="fixed inset-0 z-50 bg-zen-bg overflow-y-auto overflow-x-hidden" initial={{
    opacity: 0,
    x: '100%'
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} data-id="element-868">
      {/* NAWIGACJA GÓRNA */}
      <nav className="fixed w-full top-0 z-50 bg-zen-bg/90 backdrop-blur-md border-b border-slate-100/50" data-id="element-869">
        <div className="max-w-md mx-auto px-6 py-4 flex justify-between items-center" data-id="element-870">
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-slate-100 shadow-sm text-slate-600 hover:text-zen-text transition-colors" data-id="element-871">
            <ArrowLeft className="w-5 h-5" data-id="element-872" />
          </button>
          <div className="flex items-center space-x-2 text-zen-text" data-id="element-873">
            <Wind className="w-5 h-5 text-zen-accent" data-id="element-874" />
            <span className="font-medium tracking-wide" data-id="element-875">Butik Dobrostanu</span>
          </div>
          <div className="relative w-10 h-10 rounded-full bg-white flex items-center justify-center border border-slate-100 shadow-sm text-slate-600 cursor-pointer hover:text-zen-text" data-id="element-876">
            <ShoppingBag className="w-5 h-5" data-id="element-877" />
            {/* Kropka powiadomienia o koszyku */}
            <div className="absolute top-2 right-2 w-2 h-2 bg-emerald-500 rounded-full" data-id="element-878"></div>
          </div>
        </div>
      </nav>

      {/* KONTENER GŁÓWNY */}
      <main className="max-w-md mx-auto pt-20 pb-32" data-id="element-879">
        {/* SEKCJA ZDJĘCIA PRODUKTU */}
        <div className="w-full h-80 bg-slate-100 relative" data-id="element-880">
          <img src={product.heroImage} alt={product.name} className="w-full h-full object-cover" data-id="element-881" />
          {/* Delikatny gradient na dole zdjęcia dla płynnego przejścia */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-zen-bg to-transparent" data-id="element-882"></div>
        </div>

        {/* SEKCJA INFORMACJI O PRODUKCIE */}
        <div className="px-6 relative -mt-6" data-id="element-883">
          {/* Odznaka Nagrody (Gamification) */}
          <div className="inline-flex items-center bg-yellow-50 border border-yellow-200 px-3 py-1.5 rounded-full mb-4 shadow-sm" data-id="element-884">
            <span className="text-[10px] font-bold uppercase tracking-widest text-yellow-600 mr-2" data-id="element-885">
              Zyskujesz
            </span>
            <span className="text-xs font-bold text-yellow-700 flex items-center" data-id="element-886">
              {energyValue} punktów energii ⚡{' '}
              <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-887" />
            </span>
          </div>

          {/* Tytuł i Cena */}
          <div className="flex justify-between items-end mb-2" data-id="element-888">
            <h1 className="text-2xl font-bold text-zen-text leading-tight w-2/3" data-id="element-889">
              {product.name}
            </h1>
            <div className="text-right" data-id="element-890">
              <p className="text-2xl font-bold text-slate-800" data-id="element-891">
                {product.price.replace(' zł', '')}{' '}
                <span className="text-lg" data-id="element-892">zł</span>
              </p>
            </div>
          </div>

          <p className="text-sm text-slate-500 mb-6" data-id="element-893">{product.description}</p>

          {/* Krótki opis (Storytelling) */}
          <div className="bg-white border border-slate-100 rounded-2xl p-5 mb-8 shadow-sm" data-id="element-894">
            <p className="text-sm text-slate-600 leading-relaxed" data-id="element-895">
              {product.longDescription}
            </p>
          </div>

          {/* Synergia z aplikacją (Kluczowy element konwersji) */}
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4" data-id="element-896">
            Jak wspiera Twoją praktykę
          </h3>

          <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-5 mb-8 flex items-start space-x-4" data-id="element-897">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-898">
              <Moon className="w-6 h-6" data-id="element-899" />
            </div>
            <div data-id="element-900">
              <h4 className="font-bold text-emerald-800 mb-1 text-sm" data-id="element-901">
                {product.synergyTitle}
              </h4>
              <p className="text-xs text-emerald-700/80 leading-relaxed" data-id="element-902">
                {product.synergyDescription}
              </p>
            </div>
          </div>

          {/* Cechy produktu (Ikony) */}
          <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-903">
            {product.features.map((feature, idx) => <div key={idx} className="flex flex-col items-center text-center" data-id="element-904">
                <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 mb-2 border border-slate-100" data-id="element-905">
                  {getFeatureIcon(feature.icon)}
                </div>
                <span className="text-[10px] font-medium text-slate-600 uppercase tracking-widest" dangerouslySetInnerHTML={{
              __html: feature.label.replace(' ', '<br>')
            }} data-id="element-906"></span>
              </div>)}
          </div>

          {/* Akordeon (Rozwijane szczegóły techniczne i opinie) */}
          <Accordion title="Szczegóły techniczne" data-id="element-907">
            <ul className="space-y-2 list-disc list-inside pl-2" data-id="element-908">
              {product.specs.map((spec, idx) => {
              const [boldPart, ...rest] = spec.split(':');
              return <li key={idx} data-id="element-909">
                    {rest.length > 0 ? <>
                        <strong data-id="element-910">{boldPart}:</strong>
                        {rest.join(':')}
                      </> : spec}
                  </li>;
            })}
            </ul>
          </Accordion>

          <Accordion title="Dostawa i Zwroty" data-id="element-911">
            <p className="mb-3" data-id="element-912">
              📦{' '}
              <strong className="text-slate-700" data-id="element-913">
                Darmowa dostawa kurierem
              </strong>{' '}
              (InPost, DPD) na terenie całej Polski. Zamówienia złożone do 14:00
              wysyłamy tego samego dnia.
            </p>
            <p data-id="element-914">
              🛡️{' '}
              <strong className="text-slate-700" data-id="element-915">30 dni na testowanie.</strong>{' '}
              Chcemy, abyś czuł/a się w pełni bezpiecznie. Jeśli po 30 dniach
              nie poczujesz zauważalnej różnicy w poziomie stresu, możesz
              zwrócić produkt, a my oddamy 100% kwoty. Bez zbędnych pytań.
            </p>
          </Accordion>

          <Accordion title={`Opinie (${product.reviews.length > 0 ? '4.9/5' : 'Brak'})`} data-id="element-916">
            <div className="space-y-3" data-id="element-917">
              {product.reviews.map((review, idx) => <div key={idx} className="bg-slate-50 p-4 rounded-2xl border border-slate-100" data-id="element-918">
                  <div className="flex text-yellow-400 text-xs mb-2" data-id="element-919">
                    {[...Array(Math.floor(review.stars))].map((_, i) => <Star key={`full-${i}`} className="w-3 h-3 fill-current" data-id="element-920" />)}
                    {review.stars % 1 !== 0 && <StarHalf className="w-3 h-3 fill-current" data-id="element-921" />}
                  </div>
                  <p className="text-sm text-slate-700 font-medium mb-1" data-id="element-922">
                    "{review.title}"
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed mb-2" data-id="element-923">
                    {review.text}
                  </p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest" data-id="element-924">
                    {review.author}
                  </p>
                </div>)}
            </div>
          </Accordion>
        </div>
      </main>

      {/* STICKY BOTTOM BAR (Call to Action) */}
      <div className="fixed bottom-0 w-full bg-zen-bg/90 backdrop-blur-md border-t border-slate-100/50 pb-8 pt-4 px-6 z-50" data-id="element-925">
        <div className="max-w-md mx-auto flex items-center justify-between space-x-4" data-id="element-926">
          <div className="flex flex-col" data-id="element-927">
            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold" data-id="element-928">
              Do zapłaty
            </span>
            <span className="text-xl font-bold text-zen-text" data-id="element-929">
              {product.price}
            </span>
          </div>
          <button onClick={handleAddToCart} className="flex-1 bg-zen-text text-white rounded-full py-3.5 text-sm font-bold shadow-lg hover:bg-slate-800 transition-transform hover:scale-105 active:scale-95 flex items-center justify-center" data-id="element-930">
            <ShoppingCart className="w-5 h-5 mr-2" data-id="element-931" /> Dodaj do koszyka
          </button>
        </div>
      </div>
    </motion.div>;
}